import React, { useEffect, useMemo, useRef } from 'react';
import * as d3 from 'd3';

/**
 * A single data point for the bubble chart, containing a name and numeric value, plus arbitrary string/number attributes for dynamic grouping.
 */
export type BubbleDatum = {
  name: string;
  value: number;
  // Allow arbitrary attributes for dynamic grouping
  [key: string]: string | number | null | undefined;
};

interface SimNode extends d3.SimulationNodeDatum, BubbleDatum {
  x: number;
  y: number;
}

/**
 * Configuration props for the D3 force-driven bubble chart, including data, axis attributes, sizing, and interactivity callbacks.
 */
export type YearD3Props = {
  data: BubbleDatum[];
  // Optional: if yattr is not provided, years can be used as legacy fallback
  xattr: string; // attribute name to group on X axis (e.g., 'industry')
  yattr?: string;
  width?: number;
  height?: number;
  className?: string;
  style?: React.CSSProperties;
  motion?: Partial<MotionConfig>;
  minSize?: number;
  maxSize?: number;
  showLabels?: boolean;
  onBubbleClick?: (d: BubbleDatum) => void;
  xLabels?: string[];
  yLabels?: string[];
  axisActive: { x: boolean; y: boolean };
};

/**
 * Tunable parameters controlling the d3-force simulation behavior — decay rates, charge strength, axis pull, and alpha targets.
 */
export type MotionConfig = {
  velocityDecay: number;   // friction; lower = more glide
  chargeStrength: number;  // push/pull; higher = faster convergence
  axisStrength: number;    // pull strength toward X/Y target
  alphaOnRestart: number;  // energy injected when toggling/retargeting
  alphaDecay: number;      // how fast the simulation cools down
  alphaTarget: number;     // steady-state energy target
};

const DEFAULT_MOTION: MotionConfig = {
  velocityDecay: .82,
  chargeStrength: 5,
  axisStrength: 1.95,
  alphaOnRestart: 0.9,
  alphaDecay: 0.05,
  alphaTarget: 0.0,
};

/**
 * A d3-force-driven bubble chart component that renders interactive, animated, resizable bubbles grouped by an x/y attribute with hover/resize drag and a legend, used by Prism's Land/Renewable analysis view.
 */
export const DataBubbles: React.FC<YearD3Props> = ({
  data,
  xattr,
  yattr='year',
  width: widthProp,
  height: heightProp,
  className,
  style,
  motion,
  minSize = 20,
  maxSize = 80,
  showLabels = false,
  onBubbleClick,
  xLabels,
  yLabels,
  axisActive = { x: true, y: true },
}) => {
  const MOTION = { ...DEFAULT_MOTION, ...(motion || {}) } as MotionConfig;

  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const onBubbleClickRef = useRef<((d: BubbleDatum) => void) | undefined>(onBubbleClick);
  useEffect(() => { onBubbleClickRef.current = onBubbleClick; }, [onBubbleClick]);

  const width = widthProp ?? (typeof window !== 'undefined' ? window.innerWidth : 1200);
  const height = heightProp ?? (typeof window !== 'undefined' ? (window.innerHeight - 80) : 700);

  // Derive X categories dynamically from data and xattr unless external labels provided
  const xCategories = useMemo(() => {
    if (Array.isArray(xLabels) && xLabels.length) return [...xLabels];
    const s = new Set<string>();
    data.forEach((d) => {
      const v = d[xattr];
      if (v !== undefined && v !== null) s.add(String(v));
    });
    return Array.from(s);
  }, [data, xattr, xLabels]);

  // Y categories from external labels or derive from data
  const yCategories = useMemo(() => {
      if (Array.isArray(yLabels) && yLabels.length) return [...yLabels];
      const s = new Set<string>();
      data.forEach((d) => {
        const v = d[yattr];
        if (v !== undefined && v !== null) s.add(String(v));
      });
      return Array.from(s).sort().reverse();
  }, [data, yattr, yLabels]);

  const xScale = useMemo(() => d3.scalePoint<string>().domain(xCategories).range([150, width - 150]).padding(0.5), [xCategories, width]);
  const yScale = useMemo(() => d3.scalePoint<string>().domain(yCategories).range([50, height - 150]).padding(0.5), [yCategories, height]);

  const maxValue = useMemo(() => (data.length ? (d3.max(data, (d: BubbleDatum) => d.value) ?? 0) : 0), [data]);
  const radiusScale = useMemo(() => d3.scaleSqrt().domain([0, Math.max(maxValue, 1)]).range([minSize, maxSize]), [maxValue, minSize, maxSize]);
  const colorScale = useMemo(
    () =>
      d3
        .scaleOrdinal<string>()
        .domain(xCategories)
        .range([
          '#7CC4D4',
          '#67963F',
          '#a88877',
          '#aa97bd',
          '#ccd5ae',
          '#A253A0',
          '#3a5377',
          '#75757A',
          '#DA5DA1',
          '#C9E77B'
        ])
        .unknown('#ccc'),
    [xCategories]
  );
  const yColorScale = useMemo(
    () => d3
      .scaleOrdinal<string>()
      .domain(yCategories)
      .range(["rgba(44,62,80,0.8)", "rgba(142,68,173,0.8)", "rgba(22,160,133,0.8)", "rgba(211,84,0,0.8)", "rgba(192,57,43,0.8)", "rgba(127,140,141,0.8)"]) // stroke palette for Y groups
      .unknown('#999'),
    [yCategories]
  );

  useEffect(() => {
    if (!svgRef.current || !containerRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    svg.attr('width', width).attr('height', height);
    const defs = svg.append('defs');

    function createShadow(id: string, blur: number, offset: number, opacity: number) {
      const filter = defs
        .append('filter')
        .attr('id', id)
        .attr('height', '200%')
        .attr('width', '200%')
        .attr('x', '-50%')
        .attr('y', '-50%');
      filter
        .append('feGaussianBlur')
        .attr('in', 'SourceAlpha')
        .attr('stdDeviation', blur)
        .attr('result', 'blur');
      filter
        .append('feOffset')
        .attr('in', 'blur')
        .attr('dx', offset)
        .attr('dy', offset)
        .attr('result', 'offsetBlur');
      filter.append('feComponentTransfer').append('feFuncA').attr('type', 'linear').attr('slope', opacity);
      const merge = filter.append('feMerge');
      merge.append('feMergeNode');
      merge.append('feMergeNode').attr('in', 'SourceGraphic');
    }

    createShadow('drop-shadow', 3, 2, 0.4);
    createShadow('drop-shadow-hover', 6, 4, 0.6);

    // Small offset so the first Y band starts 10px under the X labels
    const xLabelY = 25; // matches X label y position
    const yTopStart = xLabelY + 25; // start first band 10px below X labels

    // Compute midpoints between adjacent category centers for grid lines
    const xPositions = xCategories.map((c) => xScale(c)!).sort((a, b) => a - b);
    const yPositions = yCategories.map((c) => yScale(c)!).sort((a, b) => a - b);

    const xMidpoints: number[] = [];
    for (let i = 0; i + 1 < xPositions.length; i++) {
      const a = xPositions[i];
      const b = xPositions[i + 1];
      if (a != null && b != null) xMidpoints.push((a + b) / 2);
    }
    const yMidpoints: number[] = [];
    for (let i = 0; i + 1 < yPositions.length; i++) {
      const a = yPositions[i];
      const b = yPositions[i + 1];
      if (a != null && b != null) yMidpoints.push((a + b) / 2);
    }

    // Grid lines X (vertical) at midpoints between categories
    svg
      .selectAll('.x-grid')
      .data(xMidpoints)
      .enter()
      .append('line')
      .attr('class', 'grid-line x-grid')
      .attr('x1', (d: number) => d)
      .attr('x2', (d: number) => d)
      .attr('y1', 0)
      .attr('y2', height)
      .attr('stroke', '#e0e0e0')
      .attr('stroke-width', 1)
      .style('opacity', axisActive.x ? 1 : 0);

    // // Baseline horizontal line just under the X labels
    svg
      .append('line')
      .attr('class', 'grid-line y-baseline')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', yTopStart - 15)
      .attr('y2', yTopStart - 15)
      .attr('stroke', '#e0e0e0')
      .attr('stroke-width', 1)
      .style('opacity', axisActive.y ? 1 : 0);

    // Grid lines Y (horizontal) at midpoints between categories
    svg
      .selectAll('.y-grid')
      .data(yMidpoints)
      .enter()
      .append('line')
      .attr('class', 'grid-line y-grid')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', (d: number) => d)
      .attr('y2', (d: number) => d)
      .attr('stroke', '#e0e0e0')
      .attr('stroke-width', 1)
      .style('opacity', axisActive.y ? 1 : 0);

    // Legend boxes near X-axis labels
    const xLegendBoxSize = 10;
    const xLegendPadding = 8; // space between box and text
    const xLabelBaselineY = 20;

    // Labels X: group per label, with box and text siblings
    const xLabelGroups = svg
      .selectAll<SVGGElement, string>('.x-label-group')
      .data(xCategories)
      .enter()
      .append('g')
      .attr('class', 'x-label-group')
      .attr('transform', (d: string) => `translate(${xScale(d) as number}, ${xLabelBaselineY})`);
      // Do not tie opacity to state.x so they remain visible in legend when X is off

    // Box to the left of the text
    xLabelGroups
      .append('rect')
      .attr('class', 'x-legend-box')
      .attr('x', -(xLegendBoxSize + xLegendPadding))
      .attr('y', -(xLegendBoxSize / 2))
      .attr('width', xLegendBoxSize)
      .attr('height', xLegendBoxSize)
      .attr('fill', (d: string) => colorScale(d) as string)
      .attr('stroke', '#666')
      .attr('stroke-width', 0.5);

    // Text starts at the group's origin and extends to the right
    xLabelGroups
      .append('text')
      .attr('class', 'label x-label')
      .attr('x', 0)
      .attr('y', 0)
      .attr('dy', '0.35em')
      .attr('text-anchor', 'start')
      .text((d: string) => d);

    // After text renders, measure width and center groups or lay them out as a legend when X is off
    const legendStartX = 20;
    const legendStartY = 20; // near the X label baseline area
    const legendGap = 18; // space between items
    let cursorX = legendStartX;

    xLabelGroups.each((d, i, nodes) => {
      const el = nodes[i];
      if (!el) return; // guard against undefined
      const g = d3.select(el as SVGGElement);
      const t = g.select('text').node() as SVGTextElement | null;
      const w = t ? t.getBBox().width : 0;
      if (axisActive.x) {
        const xCenter = xScale(String(d))!;
        g.attr('transform', `translate(${xCenter - w / 2}, ${xLabelBaselineY})`);
      } else {
        // Place in legend row starting from left
        g.attr('transform', `translate(${cursorX}, ${legendStartY})`);
        // Advance cursor: box width + padding + text width + gap
        cursorX += xLegendBoxSize + xLegendPadding + w + legendGap;
      }
    });

    // Labels Y positioned near the top boundary of each Y band (5px below the top, 5px from left)
    const yBandTopForCat = (cat: string): number => {
      const idx = yCategories.indexOf(cat);
      if (idx <= 0) return yTopStart + 5; // first band: 5px below yTopStart
      return (yMidpoints[idx - 1] ?? yTopStart) + 25;
    };

    svg
      .selectAll('.y-label')
      .data(yCategories)
      .enter()
      .append('text')
      .attr('class', 'label y-label')
      .attr('x', 5)
      .attr('y', (d: string) => yBandTopForCat(d))
      .attr('text-anchor', 'start')
      .text((d: string) => d)
      .style('opacity', axisActive.y ? 1 : 0);

    // Tooltip element
    let tooltipSel: d3.Selection<HTMLDivElement, unknown, null, undefined>;
    if (!tooltipRef.current) {
      tooltipSel = d3.select(containerRef.current!).append('div').attr('id', 'tooltip');
      tooltipRef.current = tooltipSel.node() as HTMLDivElement;
    } else {
      tooltipSel = d3.select(tooltipRef.current);
    }
    tooltipSel
      .style('position', 'absolute')
      .style('padding', '12px')
      .style('background', 'rgba(255, 255, 255, 0.98)')
      .style('border', '1px solid #ddd')
      .style('border-radius', '6px')
      .style('pointer-events', 'none')
      .style('opacity', '0')
      .style('font-size', '12px')
      .style('box-shadow', '0 4px 15px rgba(0, 0, 0, 0.1)')
      .style('z-index', '100')
      .style('text-align', 'left')
      .style('line-height', '1.4');

    const setTooltipPos = (ev: MouseEvent) => {
      const host = containerRef.current as HTMLDivElement | null;
      if (!host) return;
      const rect = host.getBoundingClientRect();
      const x = (ev.clientX ?? 0) - rect.left + 12; // small offset from cursor
      const y = (ev.clientY ?? 0) - rect.top - 28; // raise a bit above cursor
      tooltipSel.style('left', `${x}px`).style('top', `${y}px`);
    };

    // Nodes
    const nodes = svg
      .selectAll<SVGGElement, BubbleDatum>('.node')
      .data(data)
      .enter()
      .append('g')
      .attr('class', 'node')
      .on('click', (_event: MouseEvent, d: BubbleDatum) => {
        const cb = onBubbleClickRef.current;
        if (typeof cb === 'function') cb(d);
      })
      .on('mouseover', (event: MouseEvent, d: BubbleDatum) => {
        tooltipSel
          .style('opacity', '1')
          .html(
            `<strong>${d.name}</strong><br>${xattr}: ${String(d[xattr] ?? '')}${
              yattr ? `<br>${yattr}: ${String(d[yattr] ?? '')}` : ''
            }<br>Value: ${d.value.toLocaleString()}`
          );
        setTooltipPos(event);
        const g = d3.select<SVGGElement, BubbleDatum>(event.currentTarget as SVGGElement);
        const circleSel = g.select<SVGCircleElement>('circle');
        circleSel
          .style('filter', 'url(#drop-shadow-hover)')
          .transition()
          .duration(150)
          .attr('r', (dd: BubbleDatum) => radiusScale((dd?.value ?? d.value)) * 1.15);
      })
      .on('mousemove', (event: MouseEvent) => {
        setTooltipPos(event);
      })
      .on('mouseout', (event: MouseEvent, d: BubbleDatum) => {
        tooltipSel.style('opacity', '0');
        const g = d3.select<SVGGElement, BubbleDatum>(event.currentTarget as SVGGElement);
        const circleSel = g.select<SVGCircleElement>('circle');
        circleSel
          .style('filter', 'url(#drop-shadow)')
          .transition()
          .duration(150)
          .attr('r', (dd: BubbleDatum) => radiusScale((dd?.value ?? d.value)));
      });

    nodes
      .append('circle')
      .attr('r', (d: BubbleDatum) => radiusScale(d.value))
      .attr('fill', (d: BubbleDatum) => colorScale(String(d[xattr])) as string)
      .style('filter', 'url(#drop-shadow)')
      .style('stroke-width', '1px');

    if (showLabels) {
      nodes
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', '.35em')
        .text((d: BubbleDatum) => d.name)
        .style('fill', 'black')
        .style('font-size', '10px')
        .style('font-weight', '600')
        .style('text-shadow', '0px 1px 2px rgba(255,255,255,0.5)');
    }

    const simData: SimNode[] = data.map((d) => ({ ...d, x: width / 2, y: height / 2 }));

    const nodesWithSim = svg.selectAll<SVGGElement, SimNode>('.node');
    nodesWithSim.data(simData);

    // Build X/Y bands per category using midpoints
    const xBands: Record<string, [number, number]> = {};
    if (xPositions.length) {
      for (let i = 0; i < xCategories.length; i++) {
        const cat = xCategories[i];
        if (cat === undefined) continue;
        const left = i === 0 ? 0 : xMidpoints[i - 1] ?? 0;
        const right = i === xCategories.length - 1 ? width : xMidpoints[i] ?? width;
        xBands[String(cat)] = [left, right];
      }
    }
    const yBands: Record<string, [number, number]> = {};
    if (yPositions.length) {
      for (let i = 0; i < yCategories.length; i++) {
        const cat = yCategories[i];
        if (cat === undefined) continue;
        const top = i === 0 ? yTopStart : yMidpoints[i - 1] ?? yTopStart;
        const bottom = i === yCategories.length - 1 ? height : yMidpoints[i] ?? height;
        yBands[String(cat)] = [top, bottom];
      }
    }

    const simulation = d3
      .forceSimulation<SimNode>(simData)
      .velocityDecay(MOTION.velocityDecay)
      .alphaDecay(MOTION.alphaDecay)
      .alphaTarget(MOTION.alphaTarget)
      .force(
        'charge',
        d3.forceManyBody().strength((d, i) => (i ? 0 : (-width * 2) / 3))
      )
      .force(
        'collision',
        d3
          .forceCollide<SimNode>()
          .radius((d: SimNode) => radiusScale(d.value) + 2)
          .iterations(4)
      )
      .tick(50)
      .on('tick', () => {
        nodesWithSim.attr('transform', (d: SimNode) => {
          const r = radiusScale(d.value);
          if (axisActive.x) {
            const [minX, maxX] = xBands[String(d[xattr])] ?? [0, width];
            const clampMinX = minX + r+10;
            const clampMaxX = maxX - r - 10;
            d.x = Math.max(clampMinX, Math.min(clampMaxX, d.x));
          }
          if (axisActive.y) {
            const key = String(d[yattr!]);
            const [minY, maxY] = yBands[key] ?? [yTopStart, height];
            const clampMinY = minY + r + 10;
            const clampMaxY = maxY - r - 5;
            d.y = Math.max(clampMinY, Math.min(clampMaxY, d.y));
          }
          return `translate(${d.x}, ${d.y})`;
        });
      });

    // Define updateForces to set axis forces and toggle labels/grid visibility
    const updateForces = () => {
      simulation
        .force(
          'x',
          d3
            .forceX<SimNode>((d: SimNode) => (axisActive.x ? (xScale(String(d[xattr])) as number) : width / 2))
            .strength(axisActive.x ? MOTION.axisStrength * 1.5 : MOTION.axisStrength)
        )
        .force(
          'y',
          d3
            .forceY<SimNode>((d: SimNode) => (axisActive.y ? (yScale(String(d[yattr!])) as number) : height / 2))
            .strength(axisActive.y ? MOTION.axisStrength * 1.5 : MOTION.axisStrength)
        );

      svg.selectAll('.x-grid').style('opacity', axisActive.x ? 1 : 0);
      // Keep x-label-group visible regardless of X toggle; already positioned above
      svg.selectAll('.y-label, .y-grid, .y-baseline').style('opacity', axisActive.y ? 1 : 0);

      simulation.alpha(MOTION.alphaOnRestart).restart();
    };

    // Add drag behavior for nodes
    const drag = d3
      .drag<SVGGElement, SimNode>()
      .on('start', (event: d3.D3DragEvent<SVGGElement, SimNode, unknown>, d: SimNode) => {
        if (!event.active) simulation.alphaTarget(0.3).restart();
        d.fx = d.x;
        d.fy = d.y;
        // hide tooltip while dragging
        tooltipSel.style('opacity', '0');
      })
      .on('drag', (event: d3.D3DragEvent<SVGGElement, SimNode, unknown>, d: SimNode) => {
        let fx = event.x;
        let fy = event.y;
        const r = radiusScale(d.value);
        // Clamp drag coordinates inside bands when active, factoring in bubble radius
        if (axisActive.x) {
          const [minX, maxX] = xBands[String(d[xattr])] ?? [0, width];
          const clampMinX = minX + r;
          const clampMaxX = maxX - r;
          fx = Math.max(clampMinX, Math.min(clampMaxX, fx));
        }
        if (axisActive.y) {
          const key = String(d[yattr!]);
          const [minY, maxY] = yBands[key] ?? [yTopStart, height];
          const clampMinY = minY + r;
          const clampMaxY = maxY - r;
          fy = Math.max(clampMinY, Math.min(clampMaxY, fy));
        }
        d.fx = fx;
        d.fy = fy;
      })
      .on('end', (event: d3.D3DragEvent<SVGGElement, SimNode, unknown>, d: SimNode) => {
        if (!event.active) simulation.alphaTarget(MOTION.alphaTarget);
        d.fx = null;
        d.fy = null;
      });

    nodesWithSim.call(drag);

    // Initialize forces and visibility based on current toggle state
    updateForces();

    return () => {
      simulation.stop();
    };
  }, [data, xattr, yattr, width, height, xCategories, yCategories, xScale, yScale, radiusScale, colorScale, yColorScale, axisActive.x, axisActive.y, motion, minSize, maxSize, showLabels, MOTION.velocityDecay, MOTION.alphaDecay, MOTION.alphaTarget, MOTION.chargeStrength, MOTION.axisStrength, MOTION.alphaOnRestart]);



  return (
    <div ref={containerRef} className={className} style={{ ...style, position: 'relative' }}>
      {/* toolbar toggles removed */}
      <svg ref={svgRef} />
    </div>
  );
};

